import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatErr } from "@/lib/api";

const TEMPLATES = [
  { id: "minimal", name: "Minimal", desc: "Essenziale, stile Apple" },
  { id: "future", name: "Future", desc: "Dark glass, futuristico" },
  { id: "luxe", name: "Luxe", desc: "Nero e oro, elegante" },
  { id: "glass", name: "Glass", desc: "Vetro chiaro, tech" },
  { id: "soft", name: "Soft", desc: "Pastello, morbido" },
  { id: "bold", name: "Bold", desc: "Contrasto forte, grafico" },
  { id: "corporate", name: "Corporate", desc: "Strutturato, istituzionale" },
  { id: "creative", name: "Creative", desc: "Editoriale, caldo" },
];

export const NewCardDialog = ({ open, onOpenChange, clientId }) => {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ client_id: clientId || "", name: "Card principale", template: "minimal", prefill: true });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, client_id: clientId || f.client_id }));
    if (!clientId) api.get("/clients").then((r) => setClients(r.data));
  }, [open, clientId]);

  const submit = async () => {
    if (!form.client_id) return toast.error("Seleziona un cliente");
    setBusy(true);
    try {
      const { data } = await api.post("/cards", form);
      toast.success("Card creata come bozza");
      nav(`/admin/card/${data.id}/editor`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="new-card-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-normal">Nuova card</DialogTitle>
          <DialogDescription>Verrà creata come bozza con un indirizzo univoco.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!clientId && (
            <div>
              <Label className="text-xs">Cliente</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="new-card-client-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Nome interno della card</Label>
            <Input data-testid="new-card-name-input" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Template di partenza</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {TEMPLATES.map((t) => (
                <button key={t.id} type="button" data-testid={`new-card-template-${t.id}`} onClick={() => setForm({ ...form, template: t.id })}
                  className={`rounded-lg border p-3 text-left transition-colors ${form.template === t.id ? "border-zinc-900 bg-zinc-50" : "hover:border-zinc-400"}`}>
                  <div className="text-sm font-medium">{t.name}</div>
                  <div className="text-[11px] text-zinc-500">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center justify-between rounded-lg border px-3 py-2.5 text-sm">
            Precompila con i dati del cliente
            <Switch data-testid="new-card-prefill-switch" checked={form.prefill} onCheckedChange={(v) => setForm({ ...form, prefill: v })} />
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button onClick={submit} disabled={busy} data-testid="new-card-submit">{busy ? "Creazione…" : "Crea e apri editor"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
